import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  ShieldCheck,
  ArrowLeft,
  CreditCard,
  LockKeyhole,
} from "lucide-react";
import "./CardPayment.css";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
);

type CheckoutPaymentState = {
  bookingId: string;
  amount?: number;
  currency?: string;
  clientSecret: string;
  email?: string;
  customerName?: string;
};

function CheckoutForm({
  bookingId,
  amount,
  currency,
}: {
  bookingId: string;
  amount?: number;
  currency?: string;
}) {
  const { t, i18n } = useTranslation();

  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const formattedAmount = useMemo(() => {
    if (typeof amount !== "number") return null;

    const locale = i18n.language.startsWith("pt") ? "pt-PT" : "en-GB";

    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: (currency || "EUR").toUpperCase(),
    }).format(amount);
  }, [amount, currency, i18n.language]);

  const shortBookingId = useMemo(() => {
    if (!bookingId) return "-";
    if (bookingId.length <= 18) return bookingId;

    return `${bookingId.slice(0, 8)}...${bookingId.slice(-4)}`;
  }, [bookingId]);

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      setErrorMessage(t("cardPayment.errors.stripeLoading"));
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment/success`,
        },
        redirect: "if_required",
      });

      if (error) {
        setErrorMessage(
          error.message || t("cardPayment.errors.payment")
        );
        return;
      }

      if (
        paymentIntent &&
        (paymentIntent.status === "succeeded" ||
          paymentIntent.status === "processing" ||
          paymentIntent.status === "requires_action")
      ) {
        navigate("/payment/success", {
          state: {
            bookingId,
            amount,
            currency,
            paymentStatus: paymentIntent.status,
          },
        });

        return;
      }

      navigate("/payment/success", {
        state: {
          bookingId,
          amount,
          currency,
        },
      });
    } catch (err) {
      console.error("Erro no checkout:", err);
      setErrorMessage(t("cardPayment.errors.unexpected"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="card-payment-page">
      <div className="card-payment-shell">
        <div className="card-payment-topbar">
          <button
            type="button"
            className="card-back-inline"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={18} />
            <span>{t("cardPayment.back")}</span>
          </button>

          <div className="card-payment-trust">
            <ShieldCheck size={16} />
            <span>{t("cardPayment.securePayment")}</span>
          </div>
        </div>

        <div className="card-payment-layout">
          <div className="card-payment-main">
            <div className="card-payment-header">
              <div className="card-payment-badge">
                <CreditCard size={16} />
                <span>{t("cardPayment.checkout.badge")}</span>
              </div>

              <h1>{t("cardPayment.checkout.title")}</h1>

              <p>{t("cardPayment.checkout.subtitle")}</p>
            </div>

            <div className="payment-element-card">
              <PaymentElement />
            </div>

            {errorMessage && (
              <div className="card-payment-error">
                {errorMessage}
              </div>
            )}

            <button
              type="button"
              className="card-pay-btn"
              onClick={handleSubmit}
              disabled={isLoading || !stripe || !elements}
            >
              {isLoading
                ? t("cardPayment.buttons.processing")
                : t("cardPayment.buttons.payNow")}
            </button>
          </div>

          <aside className="card-payment-sidebar">
            <div className="summary-card">
              <div className="summary-top">
                <span className="summary-kicker">
                  {t("cardPayment.summary.title")}
                </span>

                <div className="summary-total-block">
                  <span className="summary-total-label">
                    {t("cardPayment.summary.total")}
                  </span>

                  <strong className="summary-total-value">
                    {formattedAmount || "-"}
                  </strong>
                </div>
              </div>

              <div className="summary-divider" />

              <div className="summary-details">
                <div className="summary-info-row">
                  <span>{t("cardPayment.summary.booking")}</span>

                  <strong title={bookingId}>
                    {shortBookingId}
                  </strong>
                </div>

                <div className="summary-info-row">
                  <span>{t("cardPayment.summary.currency")}</span>

                  <strong>
                    {(currency || "EUR").toUpperCase()}
                  </strong>
                </div>
              </div>

              <div className="summary-security-box">
                <div className="summary-security-icon">
                  <LockKeyhole size={15} />
                </div>

                <div className="summary-security-text">
                  <strong>
                    {t("cardPayment.summary.security.title")}
                  </strong>

                  <p>
                    {t("cardPayment.summary.security.description")}
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export function CheckoutPayment() {
  const { t } = useTranslation();

  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as CheckoutPaymentState | undefined;

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  if (!state?.clientSecret) {
    return (
      <div className="card-payment-page">
        <div className="card-payment-shell">
          <div className="card-payment-layout single">
            <div className="card-payment-main">
              <div className="card-payment-header">
                <div className="card-payment-badge">
                  <CreditCard size={16} />

                  <span>{t("cardPayment.checkout.badge")}</span>
                </div>

                <h1>{t("cardPayment.payment.title")}</h1>

                <p>{t("cardPayment.errors.paymentDataMissing")}</p>
              </div>

              <button
                type="button"
                className="card-back-btn"
                onClick={() => navigate("/booking-summary")}
              >
                {t("cardPayment.buttons.back")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
    return (
      <div className="card-payment-page">
        <div className="card-payment-shell">
          <div className="card-payment-layout single">
            <div className="card-payment-main">
              <div className="card-payment-header">
                <div className="card-payment-badge">
                  <CreditCard size={16} />

                  <span>{t("cardPayment.checkout.badge")}</span>
                </div>

                <h1>{t("cardPayment.payment.title")}</h1>

                <p>{t("cardPayment.errors.missingStripeKey")}</p>
              </div>

              <div className="card-payment-error">
                {t("cardPayment.errors.envMessage")}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: state.clientSecret,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#0f172a",
            colorBackground: "#ffffff",
            colorText: "#0f172a",
            colorDanger: "#dc2626",
            fontFamily:
              "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            spacingUnit: "4px",
            borderRadius: "16px",
          },
          rules: {
            ".Input": {
              border: "1px solid #e2e8f0",
              boxShadow: "none",
              padding: "14px 16px",
            },
            ".Input:focus": {
              border: "1px solid #0f172a",
              boxShadow: "0 0 0 4px rgba(15, 23, 42, 0.08)",
            },
            ".Tab": {
              border: "1px solid #e2e8f0",
              backgroundColor: "#ffffff",
              boxShadow: "none",
            },
            ".Tab:hover": {
              color: "#0f172a",
            },
            ".Tab--selected": {
              borderColor: "#0f172a",
              backgroundColor: "#f8fafc",
              boxShadow: "0 0 0 2px rgba(15, 23, 42, 0.04)",
            },
            ".Label": {
              color: "#475569",
              fontWeight: "500",
            },
          },
        },
      }}
    >
      <CheckoutForm
        bookingId={state.bookingId}
        amount={state.amount}
        currency={state.currency}
      />
    </Elements>
  );
}